import { useEffect, useMemo, useState } from "react";
import { CheckOutlined, CloseOutlined, SaveOutlined } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import IconPicker from "../iconPicker";
import { slugify } from "../utils";
import useStyles from "./styles";

// Modal de criação/edição de um item do catálogo do Flow (CDN ou classe de
// tráfego). Para CDNs, o identificador é normalizado (minúsculas, sem
// acentos/espaços) no momento de salvar — mesmo formato de cdnAsFilters
// (ex.: "meta"). Para classes, o identificador é usado exatamente como
// digitado (mesmo formato de classeInterfaceConditions, ex.: "TRANS/IP").
// Tanto CDN quanto Classe configuram a condição SQL completa (query),
// usada diretamente por getTrafficCdn/getTrafficClasses.
export default function ItemFormModal({
  open,
  mode = "create",
  tipo = "cdn",
  initialData = null,
  coresDisponiveis = [],
  existingIdentificadores = [],
  loading = false,
  onClose,
  onSave,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [identificador, setIdentificador] = useState("");
  const [cor, setCor] = useState(coresDisponiveis[0]?.key || "");
  const [icone, setIcone] = useState(null);
  const [iconeEmAjuste, setIconeEmAjuste] = useState(false);
  const [condicao, setCondicao] = useState("");

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setIdentificador(initialData.identificador || "");
      setCor(initialData.cor || coresDisponiveis[0]?.key || "");
      setIcone(initialData.icone || null);
      setCondicao(initialData.condicao || "");
    } else {
      setIdentificador("");
      setCor(coresDisponiveis[0]?.key || "");
      setIcone(null);
      setCondicao("");
    }
    setIconeEmAjuste(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialData]);

  const identificadorTrim = identificador.trim();

  const erros = useMemo(() => {
    const result = {};

    if (!identificadorTrim) {
      result.identificador =
        tipo === "cdn"
          ? "Informe o identificador do CDN"
          : "Informe o nome da classe";
    } else {
      const chaveComparavel = (
        tipo === "cdn" ? slugify(identificadorTrim) : identificadorTrim
      ).toLowerCase();

      if (tipo === "cdn" && !chaveComparavel) {
        result.identificador =
          "Não foi possível gerar um identificador válido a partir desse texto";
      } else if (existingIdentificadores.includes(chaveComparavel)) {
        result.identificador =
          "Já existe um item cadastrado com esse identificador";
      }
    }

    if (!condicao.trim()) {
      result.condicao = "Informe a condição de classificação";
    }

    return result;
  }, [identificadorTrim, existingIdentificadores, tipo, condicao]);

  const formValido = Object.keys(erros).length === 0 && !iconeEmAjuste;

  const handleSalvar = () => {
    if (!formValido || loading) return;

    const dados = {
      tipo,
      identificador:
        tipo === "cdn" ? slugify(identificadorTrim) : identificadorTrim,
      cor,
      icone,
      condicao: condicao.trim(),
    };

    onSave(dados);
  };

  const corSelecionada = coresDisponiveis.find((opcao) => opcao.key === cor);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" fontWeight="bold">
          {mode === "create"
            ? tipo === "cdn"
              ? "Adicionar CDN"
              : "Adicionar classe de tráfego"
            : tipo === "cdn"
              ? "Editar CDN"
              : "Editar classe de tráfego"}
        </Typography>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <Box sx={styles.iconRow}>
          <IconPicker
            value={icone}
            onChange={setIcone}
            cor={corSelecionada?.valor}
            disabled={loading}
            onAdjustingChange={setIconeEmAjuste}
          />
        </Box>
        {iconeEmAjuste && (
          <Typography sx={styles.iconAjusteHint}>
            Finalize a imagem (clique em "Usar esta imagem") antes de salvar.
          </Typography>
        )}

        <TextField
          label={tipo === "cdn" ? "Identificador do CDN" : "Nome da classe"}
          placeholder={tipo === "cdn" ? "Ex.: Meta" : "Ex.: TRANS/IP"}
          value={identificador}
          onChange={(e) => setIdentificador(e.target.value)}
          error={Boolean(erros.identificador)}
          helperText={
            erros.identificador ||
            (tipo === "cdn"
              ? "Será normalizado ao salvar (minúsculas, sem espaços) — ex.: Meta → meta"
              : "Esse nome é usado como identificador na integração")
          }
          disabled={loading}
          fullWidth
          size="small"
          sx={styles.field}
        />

        <Box sx={styles.colorSection}>
          <Typography sx={styles.sectionLabel}>Cor de destaque</Typography>
          <Box sx={styles.colorRow}>
            {coresDisponiveis.map((opcao) => (
              <Box
                key={opcao.key}
                onClick={() => !loading && setCor(opcao.key)}
                sx={{
                  ...styles.colorSwatch,
                  backgroundColor: opcao.valor,
                  ...(cor === opcao.key ? styles.colorSwatchActive : {}),
                }}
              >
                {cor === opcao.key && (
                  <CheckOutlined sx={styles.colorCheckIcon} />
                )}
              </Box>
            ))}
          </Box>
          {corSelecionada && (
            <Typography sx={styles.colorSelectedLabel}>
              {corSelecionada.label}
            </Typography>
          )}
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionLabel}>
            Condição de classificação
          </Typography>
          <Typography sx={styles.sectionHint}>
            {tipo === "cdn"
              ? "Expressão SQL usada para identificar esse CDN a partir do nome do AS (aceita LIKE e regex)."
              : "Expressão SQL usada para identificar essa classe a partir do nome da interface (aceita LIKE e regex)."}
          </Typography>
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder={
              tipo === "cdn"
                ? "Ex.: as LIKE '%GOOGLE%' OR as LIKE '%GGC%'"
                : "Ex.: interface LIKE '%TRANSIT%'"
            }
            value={condicao}
            onChange={(e) => setCondicao(e.target.value)}
            disabled={loading}
            error={Boolean(erros.condicao)}
            helperText={erros.condicao || " "}
            sx={styles.monoField}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSalvar}
          disabled={loading || !formValido}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlined />
            )
          }
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
